// OPERATOR ALERT — the "one brain" staff notification function (Jose 2026-10-24).
// A single entry point that any feature calls to alert a barber/operator: client ARRIVED, an
// EMERGENCY booking, a new MESSAGE, a WAITLIST opening, etc. It writes one `operatorAlerts` doc; the
// in-app popup (OperatorAlertPopup) listens and shows it ONLY on the device currently PINNED IN as
// the target barber (routing by who's tapped in, not a shop-wide blast — Jose's PIN-routing idea).
// Phase 2 will fan the SAME doc out to web + native push (barberPushTokens) via a Cloud Function, so
// every new alert type gets push "for free." SMS is intentionally deferred.
//
// DOCTRINE: features never build their own notification UI — they call notifyOperator({type,...}).
// One brain, many triggers (arrival / emergency / message / waitlist / take-a-message …).
import {
  collection, addDoc, onSnapshot, query, where, doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export type OperatorAlertType = 'arrival' | 'emergency' | 'message' | 'waitlist';
export type OperatorAlertTarget = 'barber' | 'shop' | 'owner';

export interface OperatorAlert {
  id: string;
  type: OperatorAlertType;
  target: OperatorAlertTarget;
  barberId?: string | null;    // target barber — popup shows only where this barber is pinned in
  barberName?: string | null;
  title: string;               // e.g. "Jose, your client has arrived"
  body?: string | null;
  clientName?: string | null;
  appointmentId?: string | null; // enables the in-popup "Start service" action
  time?: string | null;          // appointment time label
  dismissed?: boolean;
  createdAt?: any;
}

const COL = 'operatorAlerts';
const STALE_MS = 10 * 60 * 1000; // ignore an undismissed alert older than 10 min (never resurface)

/** The one-brain notifier. Writes a single alert doc; channels (in-app now, push next) read it. */
export async function notifyOperator(input: {
  type: OperatorAlertType;
  target?: OperatorAlertTarget;
  barberId?: string | null;
  barberName?: string | null;
  title: string;
  body?: string | null;
  clientName?: string | null;
  appointmentId?: string | null;
  time?: string | null;
}): Promise<void> {
  try {
    await addDoc(collection(db, COL), {
      type: input.type,
      target: input.target || (input.barberId ? 'barber' : 'shop'),
      barberId: input.barberId ?? null,
      barberName: input.barberName ?? null,
      title: input.title,
      body: input.body ?? null,
      clientName: input.clientName ?? null,
      appointmentId: input.appointmentId ?? null,
      time: input.time ?? null,
      dismissed: false,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn('[operatorAlert] notifyOperator failed (non-fatal):', e);
  }
}

/** Live subscription to RECENT, undismissed alerts. Sorted newest-first; stale ones (>10m) dropped. */
export function subscribeOperatorAlerts(cb: (alerts: OperatorAlert[]) => void): () => void {
  // where(dismissed==false) is a single-field (auto) index — no composite index needed; sort client-side.
  const q = query(collection(db, COL), where('dismissed', '==', false));
  return onSnapshot(
    q,
    (snap) => {
      const now = Date.now();
      const rows: OperatorAlert[] = [];
      snap.forEach((d) => {
        const a = d.data() as any;
        // A just-written doc's serverTimestamp is still null locally → treat as "now" (show it).
        const created = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : now;
        if (now - created > STALE_MS) return;
        rows.push({ id: d.id, ...(a as object) } as OperatorAlert);
      });
      rows.sort((x, y) => {
        const cx = x.createdAt?.toDate ? x.createdAt.toDate().getTime() : now;
        const cy = y.createdAt?.toDate ? y.createdAt.toDate().getTime() : now;
        return cy - cx;
      });
      cb(rows);
    },
    (err) => console.warn('[operatorAlert] subscribe error (non-fatal):', err)
  );
}

/** Mark an alert handled so it stops showing (on Start, Dismiss, or View). */
export async function dismissOperatorAlert(id: string): Promise<void> {
  try { await updateDoc(doc(db, COL, id), { dismissed: true }); } catch { /* non-fatal */ }
}
